import type { MapMouseEvent } from 'mapbox-gl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import useValueRef from '@alltrails/core/hooks/useValueRef';
import { MarkerProperties } from '@alltrails/types/CustomRoute';
import { useAppDispatch, useAppSelector } from '../../../redux';
import {
  selectCustomRouteActiveSegment,
  setToastMessage,
  updateActiveSegmentHandle,
  updateEndPointOfPreviousLinkedSegment,
  updateStartPointOfNextLinkedSegment
} from '../../../redux/customRoute';
import { updateCursor } from '../../../redux/reducer';
import useRerouteMarker from './useRerouteMarker';
import { getPointForHandle, validateProximityToPreviousPoint } from './validationUtils';

export const CURSOR_DRAGGING = 'grabbing';

const useDragMarker = () => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const isSmartRouting = useAppSelector(state => state.customRoute.isSmartRouting);
  const activeSegment = useAppSelector(selectCustomRouteActiveSegment);

  // State that should not trigger a re-render
  const isStartingRef = useRef(false);
  const isPendingRef = useRef(false);
  const startingMarkerRef = useRef<MarkerProperties | undefined>(undefined);
  const isSavingRef = useRef(false);

  // State that should trigger a re-render
  const [pendingMarker, setPendingMarker] = useState<MarkerProperties | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);

  const rerouteMarker = useRerouteMarker();
  const rerouteMarkerRef = useValueRef(rerouteMarker);
  const activeSegmentRef = useValueRef(activeSegment);

  const start = useCallback(
    (event: MapMouseEvent) => {
      const feature = event.features?.[0];

      if (!feature) {
        return;
      }

      isStartingRef.current = true;
      // Track the marker the mousedown event occurred on but don't set the dragging marker until the mousemove event
      startingMarkerRef.current = feature.properties as MarkerProperties;
      dispatch(updateCursor(CURSOR_DRAGGING));

      // Prevent default behavior to avoid map panning
      event.preventDefault();
    },
    [dispatch]
  );

  // Update dragging state and move the marker if dragging conditions are met
  const update = useCallback((event: MapMouseEvent) => {
    if (isStartingRef.current) {
      isPendingRef.current = true;
      isStartingRef.current = false;
    }

    if (isPendingRef.current) {
      const {
        lngLat: { lng, lat }
      } = event;
      setPendingMarker({ ...startingMarkerRef.current!, latitude: lat, longitude: lng });
    }
  }, []);

  // Save the moved marker to the redux store when the user is done dragging
  // We use a flag to trigger saving instead of doing it in the callback to prevent re-registering event handlers
  // Keep the dependency array empty to prevent re-registering event handlers
  const save = useCallback(() => {
    isStartingRef.current = false;
    isPendingRef.current = false;
    startingMarkerRef.current = undefined;
    setIsSaving(true);
  }, []);

  // Save changes when dragging a marker is done
  useEffect(() => {
    if (!isSaving) {
      return;
    }

    // Guard against re-entry ~ dispatching updateActiveSegmentHandle changes activeSegment which
    // causes rerouteMarker to be recreated. Without this guard the effect would re-fire while
    // isSaving and pendingMarker are still set, resulting in a duplicate undo stack entry.
    if (isSavingRef.current) {
      return;
    }
    isSavingRef.current = true;

    const perform = async () => {
      if (pendingMarker) {
        if (isSmartRouting) {
          await rerouteMarkerRef.current(pendingMarker);
        } else {
          const newPoint: [number, number] = [pendingMarker.latitude, pendingMarker.longitude];
          let prevPoint: [number, number] | undefined = undefined;
          const currentActiveSegment = activeSegmentRef.current;
          if (currentActiveSegment && pendingMarker) {
            const { isStart, isEnd, pathIndex: rawPathIndex, endPathIndex } = pendingMarker;
            const selectedPathIndex = isStart && isEnd ? endPathIndex! : rawPathIndex;
            const handleIndex = isEnd ? selectedPathIndex + 1 : selectedPathIndex;
            prevPoint = getPointForHandle(currentActiveSegment, handleIndex);
          }
          if (prevPoint && !validateProximityToPreviousPoint(newPoint, prevPoint)) {
            dispatch(setToastMessage({ iconCode: 'error', message: intl.formatMessage({ defaultMessage: 'Tap or draw closer to your route' }) }));
            setIsSaving(false);
            setPendingMarker(undefined);
            isSavingRef.current = false;
            return;
          }

          dispatch(updateActiveSegmentHandle({ ...pendingMarker }));
          if (pendingMarker.isStart) {
            // Don't update the undo/redo stacks since updateActiveSegmentHandle already does
            dispatch(
              updateEndPointOfPreviousLinkedSegment({
                addToUndoRedoStacks: false,
                latitude: pendingMarker.latitude,
                longitude: pendingMarker.longitude
              })
            );
          }
          if (pendingMarker.isEnd) {
            // Don't update the undo/redo stacks since updateActiveSegmentHandle already does
            dispatch(
              updateStartPointOfNextLinkedSegment({
                addToUndoRedoStacks: false,
                latitude: pendingMarker.latitude,
                longitude: pendingMarker.longitude
              })
            );
          }
        }
      }

      // Clear the save flag and the dragging marker once it is saved
      setIsSaving(false);
      setPendingMarker(undefined);
      isSavingRef.current = false;
    };

    // Run async so that we don't clear dragging marker state before the route is updated
    perform();
  }, [dispatch, pendingMarker, isSaving, isSmartRouting, rerouteMarkerRef, activeSegmentRef, intl]);

  return {
    isStartingRef,
    isPendingRef,
    pendingMarker,
    start,
    update,
    save
  };
};

export default useDragMarker;
